import React, { useState, useMemo } from 'react';
import { Col, Grid, Row, Skeleton } from 'antd';
import { useLocalStorage } from 'react-use';
import { capitalize } from 'lodash';
import { FeaturesReady, useFeatureValue } from '@growthbook/growthbook-react';
import {
  Title,
  Form,
  Input,
  PhoneInput,
  phoneInputValidations,
  FormItem,
  Text,
  Space,
} from '@vette/ui-components';
import { next, growthbook as gb } from '@vette/frontend-utils';
import {
  GrowthBookFeatures,
  phone as phoneUtils,
  stringTemplating,
  defaultInterviewNowPageFeatureValue,
} from '@vette/common-utils';
import { SessionInLocalStorage } from './types';
import {
  useSubmit,
  useSessionHandlers,
  useHandleInactiveJobOffer,
  usePrefetchJobOffer,
  InterviewNowAttributesContext,
} from './hooks';
import { FormItemNames, FormType } from './form';
import { JobOfferDetail } from './JobOfferDetail';
import DynamicButtonWrap from './DynamicButtonWrap';
import { CustomJobDetail } from './CustomJobDetail';

import * as S from './styles';

export const InterviewNowForm: React.FC<{
  activity: string[];
  clearApplicationActivities: () => void;
  isPredemo?: boolean;
  initialValues?: FormType;
  jobOfferDetail?: React.ReactNode;
  isPreview?: boolean;
  parsedApplication?: Partial<FormType>;
}> = ({
  isPredemo,
  initialValues,
  jobOfferDetail,
  isPreview,
  parsedApplication,
  activity,
  clearApplicationActivities,
}) => {
  const [form] = Form.useForm<FormType>();
  const [formDisabled, setFormDisabled] = useState(false);

  const handleInactiveJobOffer = useHandleInactiveJobOffer(
    () => setFormDisabled(true),
    isPredemo
  );

  const normalizedInitialValues: Partial<FormType> = useMemo(() => {
    if (initialValues) return initialValues;

    let providedFormValues: Partial<FormType> = {};

    if (parsedApplication) {
      providedFormValues = parsedApplication;
    }
    if (typeof window === 'undefined') return providedFormValues;

    const url = new URLSearchParams(window.location.search);

    for (const field of Object.values(FormItemNames)) {
      if (field === FormItemNames.knockoutAnswers) {
        continue;
      }

      let providedValue = url.get(field);
      if (!providedValue) {
        continue;
      }

      if (field === FormItemNames.phone) {
        providedValue = phoneUtils.normalize(providedValue);
        // checking strict equality to 12 because there is
        // no limit on length from query parameter
        if (providedValue.length !== 12) {
          continue;
        }
      } else if (
        [FormItemNames.firstName, FormItemNames.lastName].includes(field)
      ) {
        providedValue = providedValue.split(' ').map(capitalize).join(' ');
      } else if (field === FormItemNames.email) {
        providedValue = providedValue.toLowerCase();
      }

      providedFormValues[field] = providedValue;
    }

    return providedFormValues;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const { xs, sm } = Grid.useBreakpoint();

  const [sessionData, setSessionData, clearSessionData] =
    useLocalStorage<SessionInLocalStorage>('session');

  const { onSubmit, loading: submitting } = useSubmit(setSessionData);
  useSessionHandlers(sessionData, clearSessionData);

  const isFormDisabled = !!sessionData || formDisabled;

  const { loading: jobLoading } = usePrefetchJobOffer(jobOffer => {
    gb.appendAttributes({
      company: jobOffer.client.companyName,
    });
  });

  const interviewNowPageConfig = useFeatureValue(
    GrowthBookFeatures.interview_now_page_configuration,
    defaultInterviewNowPageFeatureValue
  );

  const formattedHeadline = stringTemplating.render(
    interviewNowPageConfig.form.headline,
    {}
  );

  return (
    <S.Wrapper span={24} $fillSpace={!sm} $fullHeight={!isPreview}>
      <Form
        form={form}
        initialValues={normalizedInitialValues}
        onFinish={values =>
          onSubmit(
            {
              ...values,
              activity,
            },
            () => clearApplicationActivities(),
            handleInactiveJobOffer
          )
        }
      >
        <S.CardRow gutter={[72, 36]} justify="space-between" $fillSpace={!sm}>
          <Col xs={24} lg={12}>
            <FeaturesReady timeout={5000} fallback={<Skeleton active />}>
              {interviewNowPageConfig?.jobDetail?.customHeadline ? (
                <CustomJobDetail
                  customHeadline={
                    interviewNowPageConfig.jobDetail.customHeadline as
                      | string
                      | false
                  }
                  zipCodeInput={interviewNowPageConfig.jobDetail.zipCodeInput}
                  isFormDisabled={isFormDisabled}
                />
              ) : jobOfferDetail ? (
                jobOfferDetail
              ) : (
                <next.RouterQuery fallback={<Skeleton active />}>
                  {query => (
                    <JobOfferDetail
                      args={query.args as string[]}
                      isFormDisabled={isFormDisabled}
                      form={form}
                      onJobOfferDisabled={handleInactiveJobOffer}
                    />
                  )}
                </next.RouterQuery>
              )}
            </FeaturesReady>
          </Col>

          <Col xs={24} lg={12}>
            <S.FormWrapper>
              {jobLoading && !isPreview ? (
                <Skeleton.Input active />
              ) : (
                <Title level={xs ? 5 : 4}>
                  {formattedHeadline
                    .getFlag('main')
                    .toString()
                    .split('\n')
                    .map((line, index) => (
                      <>
                        {index > 0 && <br />}
                        {line}
                      </>
                    ))}
                  {formattedHeadline.hasFlag('blue') && (
                    <Text $color="#005FF9" $weight={600}>
                      {isPredemo
                        ? ' instant call '
                        : ` ${formattedHeadline.getFlag('blue')}`}
                    </Text>
                  )}
                </Title>
              )}

              <Row gutter={6}>
                <S.ColFlex span={12}>
                  <next.ClientSide>
                    <FormItem
                      label="First name"
                      name={FormItemNames.firstName}
                      validateTrigger={['onChange', 'onBlur']}
                      rules={[
                        { required: true, message: 'First name is required' },
                      ]}
                      initialValue={normalizedInitialValues.firstName}
                      compact
                      shouldUpdate
                    >
                      <Input
                        aria-label="First name"
                        size="large"
                        disabled={isFormDisabled}
                      />
                    </FormItem>
                  </next.ClientSide>
                </S.ColFlex>
                <S.ColFlex span={12}>
                  <next.ClientSide>
                    <FormItem
                      label="Last name"
                      name={FormItemNames.lastName}
                      validateTrigger={['onChange', 'onBlur']}
                      initialValue={normalizedInitialValues.lastName}
                      compact
                      rules={[
                        { required: true, message: 'Last name is required' },
                      ]}
                    >
                      <Input size="large" disabled={isFormDisabled} />
                    </FormItem>
                  </next.ClientSide>
                </S.ColFlex>
              </Row>

              <next.ClientSide>
                <FormItem
                  label="Email"
                  name={FormItemNames.email}
                  validateTrigger={['onChange', 'onBlur']}
                  initialValue={normalizedInitialValues.email}
                  compact
                  rules={[
                    { required: true, message: 'Email is required' },
                    {
                      type: 'email',
                      message: 'Please, provide a valid email address',
                    },
                  ]}
                >
                  <Input
                    aria-label="Email"
                    type="email"
                    disabled={isFormDisabled}
                  />
                </FormItem>
              </next.ClientSide>

              <next.ClientSide>
                <FormItem
                  label="Phone number"
                  name={FormItemNames.phone}
                  validateTrigger={['onChange', 'onBlur']}
                  initialValue={normalizedInitialValues.phone}
                  compact
                  rules={phoneInputValidations}
                >
                  <PhoneInput
                    inputProps={{ 'aria-label': 'Phone number' }}
                    disabled={isFormDisabled}
                    placeholder={xs ? '' : undefined}
                  />
                </FormItem>
              </next.ClientSide>
              {/* {!interviewNowPageConfig.form.disableSpanishInterview && (
                <next.ClientSide>
                  <FormItem
                    label=""
                    name={FormItemNames.interviewInSpanish}
                    validateTrigger={['onChange', 'onBlur']}
                    initialValue={false}
                    compact
                    valuePropName="checked"
                  >
                    <Space direction="horizontal" size={12} align="center">
                      <Checkbox
                        id="spanish-language"
                        disabled={isFormDisabled}
                      />{' '}
                      <label
                        style={{
                          cursor: 'pointer',
                          textAlign: 'center',
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                        htmlFor="spanish-language"
                      >
                        Solicitar entrevista en español
                      </label>
                    </Space>
                  </FormItem>
                </next.ClientSide>
              )} */}
            </S.FormWrapper>

            {jobLoading && !isPreview ? (
              <Space $fullWidth direction="vertical" $centerVertically>
                <S.SkeletonButton size="large" active />
                <Skeleton.Input size="small" block active />
                <Skeleton.Input size="small" active />
              </Space>
            ) : (
              <InterviewNowAttributesContext.Provider
                value={{
                  isDisabled: isFormDisabled,
                  isNonInteractive: isPreview,
                  isPredemo,
                  sessionId: sessionData?.id,
                }}
              >
                <DynamicButtonWrap
                  onSessionFinish={clearSessionData}
                  submitting={submitting}
                />
              </InterviewNowAttributesContext.Provider>
            )}
          </Col>
        </S.CardRow>
      </Form>
    </S.Wrapper>
  );
};
